"use client";

import {
	IconCamera,
	IconCheck,
	IconCopy,
	IconDownload,
	IconExternalLink,
	IconFilter,
	IconSearch,
	IconTrash,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";

import { Pagination } from "@/components/shared/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { DeleteConfirmDialog } from "@/features/images/components/delete-confirm-dialog";
import { useDeleteImages } from "@/features/images/hooks/use-delete-images";
import { useImages } from "@/features/images/hooks/use-images";
import type { StoreImage } from "@/features/images/types/image.type";

export const Route = createFileRoute("/$store/_layout/images")({
	component: ImagesPage,
});

function formatDate(dateString: string): string {
	const date = new Date(dateString);
	return date.toLocaleDateString("pt-BR", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

function ImageStats({ images }: { images: StoreImage[] }) {
	const uniqueProducts = new Set(images.map((img) => img.productId)).size;
	const primaryImages = images.filter((img) => img.isPrimary).length;
	const variantLinks = images.reduce(
		(accumulator, image) => accumulator + image.usedByVariantsCount,
		0,
	);

	return (
		<div className="grid gap-4 sm:grid-cols-4">
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">
						Total de Imagens
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="text-2xl font-bold">{images.length}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">
						Produtos com Imagens
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="text-2xl font-bold">{uniqueProducts}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">
						Imagens Principais
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="text-2xl font-bold">{primaryImages}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">
						Uso em Variantes
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="text-2xl font-bold">{variantLinks}</div>
				</CardContent>
			</Card>
		</div>
	);
}

function ImageCard({
	image,
	isSelected,
	onSelect,
	onPreview,
	onDelete,
}: {
	image: StoreImage;
	isSelected: boolean;
	onSelect: () => void;
	onPreview: () => void;
	onDelete: () => void;
}) {
	return (
		<div
			className={`group relative aspect-square overflow-hidden rounded-xl border-2 bg-muted shadow-sm transition-all hover:shadow-md ${
				isSelected
					? "border-primary ring-2 ring-primary/30"
					: "border-transparent hover:border-primary/30"
			}`}
		>
			{/* Checkbox */}
			<div className="absolute left-2 top-2 z-20">
				<Checkbox
					checked={isSelected}
					onCheckedChange={onSelect}
					className="size-5 border-white bg-black/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
				/>
			</div>

			{/* Primary Badge */}
			{image.isPrimary && (
				<div className="absolute right-2 top-2 z-10">
					<Badge className="bg-primary text-primary-foreground text-[9px] px-1.5 py-0.5 shadow-sm">
						Principal
					</Badge>
				</div>
			)}

			{/* Image */}
			<button
				type="button"
				onClick={onPreview}
				className="size-full cursor-pointer"
			>
				<img
					src={image.url}
					alt={image.alt || image.productName}
					className="size-full object-cover transition-transform group-hover:scale-105"
				/>
			</button>

			{/* Hover Overlay */}
			<div className="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/70 via-black/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100">
				<div className="flex items-center justify-center gap-2 p-3">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-8 text-white hover:bg-white/20 hover:text-white"
								onClick={onPreview}
							>
								<IconExternalLink className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>Visualizar</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-8 text-white hover:bg-white/20 hover:text-white"
								onClick={() => navigator.clipboard.writeText(image.url)}
							>
								<IconCopy className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>Copiar URL</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-8 text-white hover:bg-red-500/80 hover:text-white"
								onClick={onDelete}
							>
								<IconTrash className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>Excluir</TooltipContent>
					</Tooltip>
				</div>
				<div className="px-3 pb-3">
					<p className="text-white text-xs font-medium truncate">
						{image.alt || "Sem descrição"}
					</p>
					<p className="text-white/70 text-[10px] truncate">
						{image.productName}
					</p>
				</div>
			</div>
		</div>
	);
}

function ImagePreviewDialog({
	image,
	open,
	onOpenChange,
	onDelete,
}: {
	image: StoreImage | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onDelete: () => void;
}) {
	const [copied, setCopied] = React.useState(false);

	const handleCopy = () => {
		if (image) {
			navigator.clipboard.writeText(image.url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	if (!image) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{image.alt || image.productName}
						{image.isPrimary && (
							<Badge className="bg-primary text-primary-foreground text-xs">
								Principal
							</Badge>
						)}
					</DialogTitle>
					<DialogDescription>Produto: {image.productName}</DialogDescription>
				</DialogHeader>

				<div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
					<img
						src={image.url}
						alt={image.alt || image.productName}
						className="size-full object-contain"
					/>
				</div>

				<div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
					<div>
						<p className="text-muted-foreground">Produto</p>
						<p className="font-medium">{image.productName}</p>
					</div>
					<div>
						<p className="text-muted-foreground">Uso em variantes</p>
						<p className="font-medium">{image.usedByVariantsCount}</p>
					</div>
					<div>
						<p className="text-muted-foreground">Enviada em</p>
						<p className="font-medium">{formatDate(image.createdAt)}</p>
					</div>
					<div>
						<p className="text-muted-foreground">Descrição</p>
						<p className="font-medium">{image.alt || "Sem descrição"}</p>
					</div>
				</div>

				<DialogFooter className="flex-col gap-2 sm:flex-row">
					<Button variant="outline" onClick={handleCopy}>
						{copied ? (
							<>
								<IconCheck className="mr-1.5 size-4" />
								Copiado!
							</>
						) : (
							<>
								<IconCopy className="mr-1.5 size-4" />
								Copiar URL
							</>
						)}
					</Button>
					<Button variant="outline" asChild>
						<a href={image.url} download target="_blank" rel="noreferrer">
							<IconDownload className="mr-1.5 size-4" />
							Download
						</a>
					</Button>
					<Button variant="destructive" onClick={onDelete}>
						<IconTrash className="mr-1.5 size-4" />
						Excluir
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function ImagesPage() {
	const { images, currentStoreId, isLoading, error } = useImages();
	const { deleteImages, isDeleting } = useDeleteImages(currentStoreId);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [selectedProduct, setSelectedProduct] = React.useState<string>("all");
	const [showPrimaryOnly, setShowPrimaryOnly] = React.useState(false);
	const [selectedImages, setSelectedImages] = React.useState<Set<string>>(
		new Set(),
	);
	const [previewImage, setPreviewImage] = React.useState<StoreImage | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
	const [imagesToDelete, setImagesToDelete] = React.useState<string[]>([]);
	const [currentPage, setCurrentPage] = React.useState(1);
	const imagesPerPage = 12;

	const imagesMap = React.useMemo(() => {
		return new Map(images.map((image) => [image.id, image]));
	}, [images]);

	// Get unique products for filter
	const uniqueProducts = React.useMemo(() => {
		const productMap = new Map<string, string>();
		images.forEach((img) => {
			productMap.set(img.productId, img.productName);
		});
		return Array.from(productMap.entries()).map(([id, name]) => ({
			id,
			name,
		}));
	}, [images]);

	// Filter images
	const filteredImages = React.useMemo(() => {
		return images.filter((img) => {
			const matchesSearch =
				searchQuery === "" ||
				(img.alt || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
				img.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
				img.url.toLowerCase().includes(searchQuery.toLowerCase());
			const matchesProduct =
				selectedProduct === "all" || img.productId === selectedProduct;
			const matchesPrimary = !showPrimaryOnly || img.isPrimary;
			return matchesSearch && matchesProduct && matchesPrimary;
		});
	}, [images, searchQuery, selectedProduct, showPrimaryOnly]);

	const imagesToDeleteData = React.useMemo(() => {
		return imagesToDelete
			.map((id) => imagesMap.get(id))
			.filter((image): image is StoreImage => image !== undefined);
	}, [imagesMap, imagesToDelete]);

	// Pagination
	const totalPages = Math.ceil(filteredImages.length / imagesPerPage);
	const paginatedImages = filteredImages.slice(
		(currentPage - 1) * imagesPerPage,
		currentPage * imagesPerPage,
	);

	React.useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, selectedProduct, showPrimaryOnly]);

	React.useEffect(() => {
		if (currentPage > totalPages && totalPages > 0) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	React.useEffect(() => {
		setSelectedImages((previous) => {
			const next = new Set<string>();
			let hasChanges = false;

			for (const imageId of previous) {
				if (imagesMap.has(imageId)) {
					next.add(imageId);
				} else {
					hasChanges = true;
				}
			}

			if (!hasChanges && next.size === previous.size) {
				return previous;
			}

			return next;
		});
	}, [imagesMap]);

	const toggleImageSelection = (imageId: string) => {
		setSelectedImages((prev) => {
			const next = new Set(prev);
			if (next.has(imageId)) {
				next.delete(imageId);
			} else {
				next.add(imageId);
			}
			return next;
		});
	};

	const selectAllVisible = () => {
		const visibleIds = paginatedImages.map((img) => img.id);
		setSelectedImages((prev) => {
			const next = new Set(prev);
			const allSelected = visibleIds.every((id) => next.has(id));
			if (allSelected) {
				for (const id of visibleIds) {
					next.delete(id);
				}
			} else {
				for (const id of visibleIds) {
					next.add(id);
				}
			}
			return next;
		});
	};

	const handleDeleteImages = (imageIds: string[]) => {
		setImagesToDelete(imageIds);
		setDeleteDialogOpen(true);
	};

	const confirmDelete = async () => {
		if (imagesToDelete.length === 0) {
			setDeleteDialogOpen(false);
			return;
		}

		try {
			const summary = await deleteImages(imagesToDelete);

			setSelectedImages((prev) => {
				const next = new Set(prev);
				for (const id of summary.successIds) {
					next.delete(id);
				}
				return next;
			});

			if (previewImage && summary.successIds.includes(previewImage.id)) {
				setPreviewImage(null);
			}
		} catch {
			// toast handled by hook
		} finally {
			setDeleteDialogOpen(false);
			setImagesToDelete([]);
		}
	};

	return (
		<div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-semibold text-foreground">Imagens</h1>
				<p className="text-sm text-muted-foreground">
					Visualize todas as imagens de produtos da sua loja
				</p>
			</div>

			{error ? (
				<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
					{error.message}
				</div>
			) : null}

			{isLoading ? (
				<div className="flex flex-col items-center justify-center gap-2 py-16">
					<IconCamera className="size-10 text-muted-foreground/40" />
					<p className="text-sm text-muted-foreground">Carregando imagens...</p>
				</div>
			) : null}

			{/* Stats */}
			{!isLoading ? <ImageStats images={images} /> : null}

			{/* Filters & Search */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-1 items-center gap-2">
					<div className="relative flex-1 max-w-sm">
						<IconSearch className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Buscar imagens..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9"
						/>
					</div>

					<Select value={selectedProduct} onValueChange={setSelectedProduct}>
						<SelectTrigger className="w-48" aria-label="Filtrar por produto">
							<SelectValue placeholder="Todos os produtos" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">Todos os produtos</SelectItem>
							{uniqueProducts.map((product) => (
								<SelectItem key={product.id} value={product.id}>
									{product.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="icon">
								<IconFilter className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel>Filtros</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuCheckboxItem
								checked={showPrimaryOnly}
								onCheckedChange={setShowPrimaryOnly}
							>
								Apenas principais
							</DropdownMenuCheckboxItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{selectedImages.size > 0 && !isLoading && (
					<div className="flex items-center gap-2">
						<Badge variant="secondary">
							{selectedImages.size} selecionada(s)
						</Badge>
						<Button
							variant="destructive"
							size="sm"
							onClick={() => handleDeleteImages(Array.from(selectedImages))}
							disabled={isDeleting}
						>
							<IconTrash className="mr-1.5 size-4" />
							Excluir
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setSelectedImages(new Set())}
							disabled={isDeleting}
						>
							<IconX className="mr-1.5 size-4" />
							Limpar
						</Button>
					</div>
				)}
			</div>

			{/* Select All */}
			{paginatedImages.length > 0 && !isLoading && (
				<div className="flex items-center gap-2">
					<Checkbox
						checked={
							paginatedImages.length > 0 &&
							paginatedImages.every((img) => selectedImages.has(img.id))
						}
						onCheckedChange={selectAllVisible}
					/>
					<Label className="text-sm text-muted-foreground">
						Selecionar todos visíveis
					</Label>
				</div>
			)}

			{/* Image Grid */}
			{!isLoading && paginatedImages.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<IconCamera className="size-16 text-muted-foreground/30 mb-4" />
					<h3 className="text-lg font-medium text-foreground">
						Nenhuma imagem encontrada
					</h3>
					<p className="text-sm text-muted-foreground max-w-sm mt-1">
						{searchQuery || selectedProduct !== "all" || showPrimaryOnly
							? "Tente ajustar os filtros de busca"
							: "Adicione imagens aos produtos para visualizá-las aqui"}
					</p>
				</div>
			) : !isLoading ? (
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
					{paginatedImages.map((image) => (
						<ImageCard
							key={image.id}
							image={image}
							isSelected={selectedImages.has(image.id)}
							onSelect={() => toggleImageSelection(image.id)}
							onPreview={() => setPreviewImage(image)}
							onDelete={() => handleDeleteImages([image.id])}
						/>
					))}
				</div>
			) : null}

			{/* Pagination */}
			{!isLoading && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={setCurrentPage}
					summary={
						<>
							Mostrando {(currentPage - 1) * imagesPerPage + 1} a{" "}
							{Math.min(currentPage * imagesPerPage, filteredImages.length)} de{" "}
							{filteredImages.length} imagens
						</>
					}
				/>
			)}

			{/* Preview Dialog */}
			<ImagePreviewDialog
				image={previewImage}
				open={!!previewImage}
				onOpenChange={(open) => !open && setPreviewImage(null)}
				onDelete={() => {
					if (previewImage) {
						handleDeleteImages([previewImage.id]);
					}
				}}
			/>

			{/* Delete Confirmation Dialog */}
			<DeleteConfirmDialog
				open={deleteDialogOpen}
				onOpenChange={(open) => {
					if (!isDeleting) {
						setDeleteDialogOpen(open);
					}
				}}
				onConfirm={confirmDelete}
				imagesToDelete={imagesToDeleteData}
				isDeleting={isDeleting}
			/>
		</div>
	);
}
